import { AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { SITE_CONFIG } from '@/lib/constants';

/**
 * Shipping Policy page (Taxidermy-specific)
 */
export function Shipping() {
  const lastUpdated = '16/12/2025';

  return (
    <section className="section-padding">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto">
          <span className="category-label">Legal</span>
          <h1 className="text-4xl font-bold">Shipping Policy</h1>
          <p className="mt-4 text-muted-foreground">
            Last updated: {lastUpdated}
          </p>

          {/* Important Warning */}
          <Card className="mt-8 border-destructive/20 bg-destructive/5">
            <CardContent className="p-6">
              <div className="flex gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
                  <AlertTriangle className="h-6 w-6" aria-hidden="true" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-destructive">
                    Important Notice
                  </h2>
                  <p className="mt-2 text-muted-foreground">
                    Interstate shipping of taxidermied specimens may require permits
                    under Australian wildlife protection laws. Please read this policy
                    carefully before requesting shipping.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="mt-8">
            <CardContent className="prose prose-invert max-w-none p-6 md:p-8">
              <div className="space-y-8">
                <section>
                  <h2 className="text-xl font-semibold text-foreground">1. Taxidermy Shipping Overview</h2>
                  <p className="mt-2 text-muted-foreground">
                    This shipping policy applies specifically to taxidermy specimens.
                    Due to wildlife protection regulations in Australia, shipping of
                    taxidermied specimens is subject to strict legal requirements that
                    vary by species and destination.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-foreground">2. Species-Specific Requirements</h2>
                  <div className="mt-4 space-y-4">
                    <div className="rounded-lg border border-border p-4">
                      <h3 className="font-semibold text-foreground">Native Australian Birds</h3>
                      <p className="mt-1 text-sm text-muted-foreground">
                        (e.g., Kookaburra, Cockatoo, Rosella, Magpie)
                      </p>
                      <p className="mt-2 text-muted-foreground">
                        May require state wildlife permits for interstate transport.
                        Requirements vary by state and territory.
                      </p>
                    </div>

                    <div className="rounded-lg border border-border p-4">
                      <h3 className="font-semibold text-foreground">CITES-Listed Species</h3>
                      <p className="mt-1 text-sm text-muted-foreground">
                        (Convention on International Trade in Endangered Species)
                      </p>
                      <p className="mt-2 text-muted-foreground">
                        International shipping is prohibited without valid CITES permits.
                        This includes many parrots, raptors, and other species.
                      </p>
                    </div>

                    <div className="rounded-lg border border-border p-4">
                      <h3 className="font-semibold text-foreground">Protected Species</h3>
                      <p className="mt-2 text-muted-foreground">
                        Cannot be shipped across state lines without proper documentation
                        proving legal acquisition and possession.
                      </p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-foreground">3. Buyer Responsibilities</h2>
                  <p className="mt-2 text-muted-foreground">
                    It is the buyer's responsibility to:
                  </p>
                  <ol className="mt-2 list-decimal pl-5 text-muted-foreground space-y-2">
                    <li>Verify permit requirements for their state/territory</li>
                    <li>Obtain all necessary permits before shipping is arranged</li>
                    <li>Comply with all applicable wildlife regulations</li>
                    <li>Provide any required documentation to us before shipping</li>
                    <li>Cover the cost of any required permits or certificates</li>
                  </ol>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-foreground">4. State Wildlife Authorities</h2>
                  <p className="mt-2 text-muted-foreground">
                    We recommend contacting your state's wildlife authority for guidance:
                  </p>
                  <ul className="mt-4 space-y-2 text-muted-foreground">
                    <li>
                      <strong className="text-foreground">NSW:</strong> Department of Planning
                      and Environment
                    </li>
                    <li>
                      <strong className="text-foreground">VIC:</strong> Department of Energy,
                      Environment and Climate Action
                    </li>
                    <li>
                      <strong className="text-foreground">QLD:</strong> Department of Environment
                      and Science
                    </li>
                    <li>
                      <strong className="text-foreground">WA:</strong> Department of Biodiversity,
                      Conservation and Attractions
                    </li>
                    <li>
                      <strong className="text-foreground">SA:</strong> Department for Environment
                      and Water
                    </li>
                    <li>
                      <strong className="text-foreground">TAS:</strong> Department of Natural
                      Resources and Environment Tasmania
                    </li>
                    <li>
                      <strong className="text-foreground">NT:</strong> Department of Environment,
                      Parks and Water Security
                    </li>
                    <li>
                      <strong className="text-foreground">ACT:</strong> Environment, Planning and
                      Sustainable Development Directorate
                    </li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-foreground">5. Shipping Methods</h2>
                  <p className="mt-2 text-muted-foreground">
                    When shipping is arranged with proper permits:
                  </p>
                  <ul className="mt-2 list-disc pl-5 text-muted-foreground space-y-1">
                    <li>Specimens are carefully packaged to prevent damage</li>
                    <li>Insurance is recommended for valuable pieces</li>
                    <li>Shipping costs are the responsibility of the buyer</li>
                    <li>Tracking information will be provided</li>
                    <li>Local pickup is available and encouraged</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-foreground">6. Our Liability</h2>
                  <p className="mt-2 text-muted-foreground font-semibold text-foreground">
                    We are not responsible for specimens confiscated due to missing
                    or invalid permits.
                  </p>
                  <p className="mt-2 text-muted-foreground">
                    By requesting shipping, you confirm that you have verified all
                    legal requirements and obtained necessary permits for receiving
                    the specimen in your jurisdiction.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-foreground">7. Contact</h2>
                  <p className="mt-2 text-muted-foreground">
                    For questions about shipping requirements or to discuss your
                    specific situation, please contact us at{' '}
                    <a
                      href={`mailto:${SITE_CONFIG.contact.email}`}
                      className="text-primary hover:underline"
                    >
                      {SITE_CONFIG.contact.email}
                    </a>
                    .
                  </p>
                </section>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
